import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  ArrowLeft, MapPin, Star, Globe, Calendar, Languages, 
  Banknote, CheckCircle, Code2, Lock 
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png"
};

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const DestinationDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDestination = async () => {
      try {
        const response = await axios.get(`${API}/destinations/${id}`);
        setDestination(response.data);
      } catch (err) {
        setError("Destination not found");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchDestination();
    else setLoading(false);
  }, [id, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !destination) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA]">
        <h1 className="font-heading text-3xl font-bold text-[#0B132B] mb-4">Destination Not Found</h1>
        <Link 
          to="/#directory" 
          className="text-[#D4AF37] hover:text-[#92710d] flex items-center gap-2"
          data-testid="back-to-directory-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </Link>
      </div>
    );
  }

  const hasCoordinates = destination.latitude && destination.longitude;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="destination-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/#directory" 
            className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#0B132B] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Directory</span>
          </Link>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img 
          src={destination.image_url} 
          alt={destination.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Rating Badge */}
        <div className="absolute top-6 right-6">
          <img 
            src={BADGE_URLS[destination.rating]} 
            alt={`${destination.rating} rating`}
            className="w-20 h-20 drop-shadow-lg"
          />
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border mb-4 ${
              destination.rating === "gold" 
                ? "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" 
                : destination.rating === "silver" 
                ? "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]" 
                : "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]"
            }`}>
              <Star className="w-4 h-4" />
              {destination.rating.charAt(0).toUpperCase() + destination.rating.slice(1)} Quality Destination
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="destination-name">
              {destination.name}
            </h1>
            <div className="flex items-center gap-2 text-white/90">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">{destination.country}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white p-8 border border-gray-100">
              <h2 className="font-heading text-2xl font-bold text-[#0B132B] mb-4">About This Destination</h2>
              <p className="text-[#4A5568] leading-relaxed">
                {destination.full_description || destination.description}
              </p>
            </div>

            {/* Highlights */}
            {destination.highlights && destination.highlights.length > 0 && (
              <div className="bg-white p-8 border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-[#0B132B] mb-6">Highlights & Attractions</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {destination.highlights.map((highlight) => (
                    <div key={highlight} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-[#4A5568]">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Map */}
            {hasCoordinates && (
              <div className="bg-white p-8 border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-[#0B132B] mb-6">Location</h2>
                <div className="h-[400px] rounded-lg overflow-hidden" data-testid="destination-map">
                  <MapContainer 
                    center={[destination.latitude, destination.longitude]} 
                    zoom={10} 
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={[destination.latitude, destination.longitude]}>
                      <Popup>
                        <strong>{destination.name}</strong><br />
                        {destination.country}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div className="bg-white p-6 border border-gray-100">
              <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-4">Destination Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-[#718096]">Country</p>
                    <p className="text-[#0B132B] font-medium">{destination.country}</p>
                  </div>
                </div>
                {destination.best_time_to_visit && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Best Time to Visit</p>
                      <p className="text-[#0B132B] font-medium">{destination.best_time_to_visit}</p>
                    </div>
                  </div>
                )}
                {destination.currency && (
                  <div className="flex items-center gap-3">
                    <Banknote className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Currency</p>
                      <p className="text-[#0B132B] font-medium">{destination.currency}</p>
                    </div>
                  </div>
                )}
                {destination.language && (
                  <div className="flex items-center gap-3">
                    <Languages className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Language</p>
                      <p className="text-[#0B132B] font-medium">{destination.language}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Certification Card */}
            <div className="bg-[#0B132B] p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src={BADGE_URLS[destination.rating]} 
                  alt={`${destination.rating} rating`}
                  className="w-16 h-16"
                />
                <div>
                  <h3 className="font-heading text-xl font-bold">
                    {destination.rating.charAt(0).toUpperCase() + destination.rating.slice(1)} Certified
                  </h3>
                  <p className="text-white/70 text-sm">WTTQ Quality Label</p>
                </div>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                This destination has been certified by WTTQ.Org for meeting our rigorous quality standards in tourism excellence.
              </p>
              <Link
                to={user ? `/embed/destination/${destination.id}` : "/signin"}
                state={!user ? { from: `/embed/destination/${destination.id}` } : undefined}
                className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#D4AF37] text-[#0B132B] font-semibold text-sm hover:bg-[#E8C456] transition-colors"
                data-testid="get-embed-badge-button"
                title={user ? "Get embed badge" : "Partner-only — sign in to access"}
              >
                {user ? <><Code2 className="w-4 h-4" /> Get Embed Badge</> : <><Lock className="w-4 h-4" /> Sign in for Embed Badge</>}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0B132B] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} WTTQ.Org. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DestinationDetailPage;
